import { useEffect, useRef, type ReactNode } from 'react';

/**
 * The app's modal dialog — a real `<dialog>` element opened with `showModal()`.
 *
 * WHY THE NATIVE ELEMENT and not another `.confirm-overlay` div. `showModal()` puts
 * the dialog in the top layer and marks everything else inert: the focus trap, the
 * "you cannot click the page behind it", the `::backdrop` and the return of focus to
 * whatever opened it are the browser's, not three hand-written effects that have to
 * agree. `ConfirmDialog` and `AgentRemoveDialog` predate this and are left alone —
 * they are yes/no cards, and this is the one place a form lives inside a modal.
 *
 * NO BACKDROP-CLICK CLOSE, deliberately, and unlike those two. A credential is typed
 * in here. A stray click on the page next to a form is a mis-click; losing a pasted
 * DO token and a half-filled wizard to one is not a recoverable one, and there is
 * nothing on screen afterwards that says what happened. Escape and Cancel are the
 * two exits, both explicit.
 *
 * ESCAPE IS CAUGHT IN CAPTURE PHASE AND STOPPED. This dialog is opened from a screen
 * that can itself be inside `Drawer`, which closes on a bubbling Escape — one press
 * would otherwise dismiss the dialog AND navigate the drawer away behind it, which is
 * the exact bug `AgentRemoveDialog:138-152` documents. One press closes one layer.
 *
 * CHILDREN ARE MOUNTED ONLY WHILE OPEN, so every reopen starts on step 1 with empty
 * fields — a wizard that remembers a half-typed credential from the last time it was
 * cancelled is a surprise, not a convenience.
 */
export function Dialog({
  open,
  onClose,
  title,
  titleId,
  size = 'md',
  busy = false,
  children,
}: {
  open: boolean;
  /** Called for every dismissal path: Escape, Cancel, and the DOM `close` event. */
  onClose: () => void;
  title: string;
  /** Id of the visible heading, so the dialog's accessible name IS the heading. */
  titleId: string;
  size?: 'sm' | 'md' | 'lg';
  /** While true, Escape and Cancel are refused — a create is in flight. */
  busy?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  // Focus the field the caller nominates, else the first focusable thing. The browser
  // focuses the first focusable element on `showModal()` already; this exists because
  // that is usually the wrong one — on step 1 it would be the Cancel button.
  useEffect(() => {
    if (!open) return;
    const el = ref.current;
    if (!el) return;
    const target =
      el.querySelector<HTMLElement>('[data-autofocus]') ??
      el.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
    target?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (busy) {
        // A create is in flight; the dialog must not vanish out from under it, and
        // the native close-watcher would do exactly that.
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      // A control INSIDE the dialog that has its own popup open owns this press —
      // an expanded combobox closes ITS LIST, not the form the list sits in. This
      // listener runs first (capture, on window), so stopping the event here would
      // make that widget's own Escape handler unreachable; the exemption hands the
      // press back to it. `ComboBox` then stops and preventDefaults the event, so
      // neither this dialog nor a Drawer behind it sees it. A control that expands
      // but does NOT handle Escape falls through to the native close-watcher and
      // `onCancel` below — the same close as before.
      const target = e.target;
      if (
        target instanceof Element &&
        target.getAttribute('role') === 'combobox' &&
        target.getAttribute('aria-expanded') === 'true'
      ) {
        return;
      }
      // Capture phase, and it stops here: the Drawer this may be rendered inside
      // closes on Escape too, and one press must close exactly one layer.
      e.stopPropagation();
      // `preventDefault` here, `onClose` below: closing through React keeps `open`
      // and the element's own state from disagreeing.
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, busy, onClose]);

  return (
    <dialog
      ref={ref}
      className={`app-dialog app-dialog--${size}`}
      aria-labelledby={titleId}
      /* The element's own cancel/close events, for the paths JS did not start:
         a browser close-watcher gesture, or `close()` from anywhere else. */
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onClose();
      }}
      onClose={() => {
        if (open) onClose();
      }}
      /* Tab and Escape must not reach a Drawer behind this. The top layer already
         traps Tab; this is the React-tree half, for the bubbling listeners. */
      onKeyDown={(e) => {
        if (e.key === 'Tab' || e.key === 'Escape') e.stopPropagation();
      }}
    >
      {open && (
        <>
          <div className="dialog-head">
            <h2 id={titleId}>{title}</h2>
          </div>
          {children}
        </>
      )}
    </dialog>
  );
}
